import * as yup from "yup";
import { FilterDefinition } from "components/Shared/FilterEditor/utils";
import {
  createMakeGetManyFilterResult,
  getDateTimeFilterDefinition,
  getSearchFilterDefinition,
  getSelectFilterDefinition,
  getStringFilterDefinition,
  getStringSelectFilterDefinition,
  SEARCH_FILTER_ID,
} from "components/Shared/FilterEditor/standardFilters";
import {
  BrandOrderingPlatformFilter,
  DeliveryMethod,
} from "__generated__/gql/graphql";
import ProductLineSelect from "components/Shared/Select/ProductLineSelect";
import SelectSkuMenu from "components/Shared/Select/SelectSkuMenu";
import getBaseFilterDefinitions from "components/Shared/OrderingPlatformDirectory/filterDefinitions";
import createNestedFilterDefinitions from "components/Shared/FilterEditor/createNestedFilterDefinitions";
import { assertIsString } from "utils/assertions";
import { makeEnsureType } from "utils/typescriptUtilis";
import EnumSelect from "components/Shared/Select/EnumSelect";
import { deliveryMethodLabelsByType } from "components/Shared/DeliveryMethodButtons";
import { getEnumSchema } from "utils/schemas/utils";

const filterObj = makeEnsureType<BrandOrderingPlatformFilter>();

const makeGetManyResult =
  createMakeGetManyFilterResult<BrandOrderingPlatformFilter>();

const orderingPlatformFilters = createNestedFilterDefinitions(
  "orderingPlatform",
  getBaseFilterDefinitions(),
  { groupLabel: "Customer" },
);

const orderingPlatformSearchFilter = orderingPlatformFilters.find((f) =>
  f.id.endsWith(SEARCH_FILTER_ID),
);

export const filterDefinitions: FilterDefinition[] = [
  getSearchFilterDefinition({
    getResult: (value, context) => {
      assertIsString(value);

      const ors: BrandOrderingPlatformFilter[] = [
        { supplierName: { includesInsensitive: value } },
        { supplierCode: { includesInsensitive: value } },
      ];

      const opSearch = orderingPlatformSearchFilter?.getResult?.(
        value,
        context,
      );
      if (opSearch) ors.push(opSearch);

      if (ors.length === 0) return;

      return filterObj({ or: ors });
    },
  }),
  getStringFilterDefinition({
    id: "supplierName",
    label: "Supplier Name",
  }),
  getStringFilterDefinition({
    id: "supplierCode",
    label: "Supplier Code",
  }),
  getSelectFilterDefinition({
    id: "deliveryMethod",
    label: "Delivery Method",
    getSelectResult: (
      value,
      { filterVariantDefinition: { id: variantId } },
    ) => ({ deliveryMethod: { [variantId]: value } }),
    selectSchema: getEnumSchema<DeliveryMethod>(DeliveryMethod),
    renderSelectInput: ({ field }, { isMulti }) => (
      <EnumSelect
        {...field}
        isMulti={isMulti}
        menuPortalTarget={document.body}
        enum={DeliveryMethod}
        labelsByValue={deliveryMethodLabelsByType}
      />
    ),
  }),
  getDateTimeFilterDefinition({
    id: "createdAt",
    label: "Added at",
    defaultVariant: "greaterThan",
  }),
  ...orderingPlatformFilters,
];

const filterDefinitionsWithProducts: FilterDefinition[] = [
  ...filterDefinitions,
  getStringSelectFilterDefinition({
    id: "productLine",
    label: "Product Line",
    getResult: makeGetManyResult("skuBrandOrderingPlatforms", (f) => ({
      sku: { productLine: { name: f } },
    })),
    getSelectResult: makeGetManyResult("skuBrandOrderingPlatforms", (f) => ({
      sku: { productLineId: f },
    })),
    selectSchema: yup.string().uuid(),
    renderSelectInput: ({ field }, { isMulti }) => (
      <ProductLineSelect
        {...field}
        isMulti={isMulti}
        menuPortalTarget={document.body}
      />
    ),
  }),
  getStringSelectFilterDefinition({
    id: "sku",
    label: "SKU",
    getResult: makeGetManyResult("skuBrandOrderingPlatforms", (f) => ({
      sku: { name: f },
    })),
    getSelectResult: makeGetManyResult("skuBrandOrderingPlatforms", (f) => ({
      skuId: f,
    })),
    selectSchema: yup.string().uuid(),
    renderSelectInput: ({ field }, { isMulti }) => (
      <SelectSkuMenu
        {...field}
        isMulti={isMulti}
        menuPortalTarget={document.body}
      />
    ),
  }),
];

export default filterDefinitionsWithProducts;
